import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import Draggable from "gsap/Draggable";
import CameraManager from "../../manager/CameraManager";
import useFlowStore from "../../store/flowStore";
import useCursorStore from "../../store/cursorStore";
import { audios, images } from "../../../assets/assets";
import * as THREE from "three";
import { RectAreaLightHelper } from "three/examples/jsm/helpers/RectAreaLightHelper.js";

gsap.registerPlugin(Draggable);

export default function DesktopUI({ scene, audioEnd, onDesktopClick }) {
  const mailRef = useRef();
  const reveCta = useRef();
  const topbarRef = useRef();

  const isOpenRef = useRef(false);
  const isFullscreenRef = useRef(false);

  const { nextStep, playCurrentAudio, playAudioFile, audioSrc } =
    useFlowStore.getState();
  const { setCursorTheme, setCursorInteractive } = useCursorStore.getState();

  const [visibleParagraphs, setVisibleParagraphs] = useState([]);
  const [ctaEnabled, setCtaEnabled] = useState(false);

  // --- NEW: état pour le compteur animé ---
  const [displayedFragments, setDisplayedFragments] = useState(5012);

  const correctAudio = audios.mail_superflamme;

  const interactUIRef = useFlowStore((state) => state.interactUIRef);

  // ✅ Déclenche la révélation progressive du texte
  useEffect(() => {
    if (audioSrc !== correctAudio) return;

    const revealMap = [
      { index: 0, time: 0 },
      { index: 1, time: 1 },
      { index: 2, time: 7 },
      { index: 3, time: 14 },
      { index: 4, time: 20 },
      { index: 5, time: 34 },
    ];

    const timers = revealMap.map(({ index, time }, i) =>
      setTimeout(() => {
        setVisibleParagraphs((prev) => {
          const next = [...prev, index];
          return next;
        });
      }, time * 1000)
    );

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc]);

  useEffect(() => {
    if (audioEnd === true) {
      setCtaEnabled(true);
      // ANIMATE REVE BUTTON YOYO
      if (reveCta.current) {
        gsap.fromTo(
          reveCta.current,
          { scale: 1 },
          {
            scale: 1.15,
            duration: 0.15,
            yoyo: true,
            repeat: 1,
            ease: "power1.inOut",
          }
        );
      }
    }
  }, [audioEnd]);

  // --- NEW: Fonction pour animer le compteur et activer le CTA ---
  const animateFragments = (from, to, duration = 1.2, onComplete) => {
    const obj = { val: from }; // ← variable locale
    gsap.to(obj, {
      val: to,
      duration,
      ease: "power1.inOut",
      onUpdate() {
        setDisplayedFragments(Math.floor(obj.val));
      },
      onComplete,
    });
  };

  // --- NEW: Fonction d'activation animée du CTA ---
  const enableCtaWithCountDown = (onEnd) => {
    animateFragments(5012, 12, 1.2, () => {
      // Petite anim sur le bouton pour attirer l'attention
      onEnd?.();
    });
  };

  // ✅ Affichage du mail
  const handleOpenMail = () => {
    if (isOpenRef.current) return;
    isOpenRef.current = true;

    gsap.fromTo(
      mailRef.current,
      { scale: 0, autoAlpha: 0 },
      {
        scale: 1,
        autoAlpha: 1,
        duration: 0.6,
        ease: "power3.out",
        force3D: true,
        onComplete: () => {
          Draggable.create(mailRef.current, {
            type: "x,y",
            bounds: ".desktopUI",
            edgeResistance: 1,
            cursor: "grab",
            trigger: topbarRef.current,
          });
        },
      }
    );
  };

  const handleCloseMail = () => {
    gsap.to(mailRef.current, {
      scale: 0,
      opacity: 0,
      duration: 0.4,
      ease: "power2.inOut",
      force3D: true,
      onComplete: () => {
        isOpenRef.current = false;
      },
    });
  };

  const toggleFullscreen = () => {
    if (!mailRef.current) return;
    isFullscreenRef.current = !isFullscreenRef.current;

    gsap.to(mailRef.current, {
      width: isFullscreenRef.current ? "100%" : "850px",
      height: isFullscreenRef.current ? "100%" : "450px",
      top: isFullscreenRef.current ? 0 : "50%",
      left: isFullscreenRef.current ? 0 : "auto",
      right: isFullscreenRef.current ? 0 : "2rem",
      transform: isFullscreenRef.current ? "none" : "translateY(-50%)",
      borderRadius: isFullscreenRef.current ? 0 : "8px",
      duration: 0.5,
      ease: "power2.inOut",
    });
  };

  const handleReveCta = async () => {
    if (interactUIRef?.current?.exit) {
      await interactUIRef.current.exit();
    }
    setCursorInteractive(false);

    setCursorTheme("");

    let playAnimationOnce = false;
    enableCtaWithCountDown(() => {
      if (playAnimationOnce) return;
      playAnimationOnce = true;
      // GOING STEP 3
      CameraManager.resetLookTargetWithAnimation({
        duration: 1,
        onComplete: () => {
          nextStep(() => {
            // PLAY SWOOSH CAMERA_3 SOUND : GOING TO LARGE ANGLE
            const swooshAudio = new Audio(audios.swoosh_computer_out);
            swooshAudio.play().catch(console.warn);
            // SHOW DOOR LILIAN
            const doorVierge = scene.getObjectByName("module_porte_vierge");
            doorVierge.visible = false;

            // RESET DOOR LILIAN
            const doorAnim = scene.getObjectByName("module_porte_lilian_anim_for_bake");
            doorAnim.children.forEach(child => {
              child.visible = true;
          
              
              const Mesh = child.getObjectByName("light");
              
              
              if (Mesh) {
                // Crée la RectArea avec la même taille
                const area = new THREE.RectAreaLight(0xffffff, 7, 2.5, 1);
                area.position.set(0, -0.05, 0);
                area.rotation.set(Math.PI / 2, 0, Math.PI / 2);
                Mesh.add(area); 
                Mesh.material.opacity = 0;
                Mesh.material.transparent = true;
                Mesh.material.depthWrite = false;
                const helper = new RectAreaLightHelper(area);
                area.add(helper);
               }
            });
            

            // PLAY CAMERA_3 ANIMATION : GOING TO LARGE ANGLE
            console.log("play animation Camera_3");
            let playAnimationOnce = false;
            CameraManager.playAnimation(() => {
              if (playAnimationOnce) return;
              playAnimationOnce = true;

              CameraManager.reset();
              CameraManager.switchCamera("camera_4");

              setTimeout(() => {
                CameraManager.enableMouseLook(1);
              }, 50);

              // PLAY DOOR CLICKING ANIMATION AUDIO
              playCurrentAudio(() => {
                setCursorInteractive(true);
                // TOGGLE DOOR INTERACT UI
                useFlowStore.getState().setShowInteractUI(true);

                // 🔒 Désactiver le bureau
                const desktopHitbox =
                  scene.getObjectByName("module_desk_hitbox");
                if (desktopHitbox) {
                  desktopHitbox.userData.isClickable = false;
                  desktopHitbox.userData.onPointerOver = null;
                  desktopHitbox.userData.onPointerOut = null;
                }

                // ✅ Activer la porte
                const doorHitbox = scene.getObjectByName("door_module_hitbox");
                if (doorHitbox) {
                  doorHitbox.userData.isClickable = true;
                }
              });
            }, 5000);
          });
        },
      });
    });
  };

  const mailParagraphs = [
    <p key="p1">Bonjour,</p>,
    <p key="p2">
      Je vous écris pour mon petit fils
      Lilian. Il a toujours été <strong>fasciné par l'espace.</strong>
    </p>,
    <p key="p3">
      Depuis son plus jeune âge,{" "}
      il dessine des fusées, observe les étoiles et{" "}
      <strong>s'imagine des missions sur la Lune.</strong>
    </p>,
    <p key="p4">
      Malheureusement, il est gravement malade,
      mais il continue de rêver.
    </p>,
    <p key="p5">
      <strong>Ce rêve, c'est plus qu'un jeu. </strong>
      C'est ce qui le pousse à se lever tous les jours
      où les traitements sont difficiles. Je sais qu'on
      ne peut pas aller sur la lune, mais je me suis dit que vous pourriez
      l'en rapprocher.
    </p>,
    <p key="p6">
      Merci pour tout ce que vous faites,
      <br />
      Le papy de Lilian.
    </p>
  ];

  const mails = [
    {
      author: "Marcel Dubois",
      subject: "Rêve d’aller sur la Lune",
      preview: "Bonjour, je m'appelle Marcel Dubois et je vous écris...",
    },
    {
      author: "Camille Bernard",
      subject: "Rêve d’écrire un livre sur sa maladie",
      preview:
        "Bonjour, Je vous écris pour ma fille Zoé. Elle a 11 ans. Depuis son diagnostic, elle passe ses journées à écrire. C’est sa façon à elle…",
    },
    {
      author: "Élodie Lefèvre",
      subject: "Rêve de rencontrer Killian Mbappé",
      preview:
        "Bonjour, Mon neveu Sami est un passionné de foot. Il connaît toutes les stats de la Ligue 1 et dort avec un maillot du PSG. Il garde une…",
    },
    {
      author: "Élodie Lefèvre",
      subject: "Rêve de rencontrer Killian Mbappé",
      preview:
        "Bonjour, Mon neveu Sami est un passionné de foot. Il connaît toutes les stats de la Ligue 1 et dort avec un maillot du PSG. Il garde une…",
    },
    {
      author: "Manon Rousseau",
      subject: "Rêve d’une guitare personnalisé",
      preview:
        "Bonjour, J’aimerais vous parler de Tom. Il a 14 ans et une passion folle pour la musique. Même à l’hôpital, il gratte ses accords avec…",
    },
    {
      author: "Julien Caron",
      subject: "Rêve de voyager à Disneyland",
      preview:
        "Bonjour, Je suis la maman de Chloé. Elle a toujours rêvé de rencontrer ses princesses préférées. Même pendant ses chimios, elle regarde…",
    },
    {
      author: "Chloé Laurent",
      subject: "Rêve de sauter en parachute",
      preview:
        "Bonjour,Mon petit-fils Émilien a toujours la tête dans les nuages. Il parle souvent de voler, de sentir le vent sur son visage. Il garde une…",
    },
    // Ajoutez d'autres objets mail ici
  ];

  // 📦 Ouverture automatique après 3s
  useEffect(() => {
    const timer = setTimeout(() => {
      handleOpenMail();
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="desktopUI" onClick={onDesktopClick}>
      <img
        src={images.mailDesktopBackground}
        alt="Arrière plan de l'écran d'ordinateur"
        className="desktopUI__background"
      />

      <button
        type="button"
        className="desktopUI__mailbutton"
        onClick={handleOpenMail}
      >
        <img
          src={images.mailDesktopIcon}
          className="desktopUI__mailbutton__icon"
          alt="Icône de l'application mail"
        />
      </button>

      <div className="desktopUI__mail" ref={mailRef}>
        <div className="desktopUI__mail__left">
          <div className="desktopUI__mail__topbar" ref={topbarRef}>
            <div className="dot red" onClick={handleCloseMail}></div>
            <div className="dot yellow"></div>
            <div className="dot green" onClick={toggleFullscreen}></div>
          </div>
          <div className="desktopUI__mail__left__fragment">
            <img src={images.fragmentSvg} alt="Image d'un fragment" />
            {/* --- CHANGED: affichage du compteur animé --- */}
            <span>{displayedFragments}</span>
            <p>Fragments d’étoiles</p>
          </div>
          <div className="desktopUI__mail__left__title">Tous les mails</div>
          <div className="desktopUI__mail__left__infos">
            367 messages, 1 non-lu
          </div>
          <div className="desktopUI__mail__left__list">
            {mails.map((mail, i) => (
              <div className="desktopUI__mail__left__item" key={i}>
                <strong>{mail.author}</strong>
                <div className="desktopUI__mail__left__item__subject">
                  {mail.subject}
                </div>
                <div className="desktopUI__mail__left__item__preview">
                  {mail.preview}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="desktopUI__mail__right">
          <div className="desktopUI__mail__header">
            <div className="desktopUI__mail__profile">
              <img src={images.photo} alt="Marie" />
              <div className="desktopUI__mail__profile__infos">
                <div className="desktopUI__mail__profile__name">
                  Marcel Dubois
                </div>
                <div className="desktopUI__mail__profile__subject">
                  Rêve d’aller sur la Lune
                </div>
                <div className="desktopUI__mail__profile__to">
                  <strong>To:</strong> reves@reves.fr
                </div>
              </div>
            </div>
            <div className="desktopUI__mail__cta">
              <button
                className="desktopUI__mail__cta__button"
                ref={reveCta}
                onClick={handleReveCta}
                disabled={!ctaEnabled}
              >
                Réaliser le rêve pour 5000
                <img src={images.fragmentSvg} alt="Image d'un fragment" />
              </button>
            </div>
          </div>

          <div className="desktopUI__mail__body">
            {mailParagraphs.map((paragraph, index) => (
              <div
                key={index}
                style={{
                  opacity: visibleParagraphs.includes(index) ? 1 : 0,
                  transition: "opacity 1s ease-in-out",
                }}
              >
                {paragraph}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
